import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { Button } from 'components/buttons'
import { Field } from 'components/form/field'
import { Input } from 'components/form/input'
import { Spinner } from 'components/loaders'
import { useWorkdayProfile } from './hooks'
import { WorkdayProfileAttribute, WorkdayValue } from './types'
import {
  WorkdayDataTable,
  WorkdayValueCell,
  compareWorkdayValues,
} from './WorkdayDataTable'

interface Props {
  employeeId: number
  date: DateTime
  onDateChange: (date: DateTime) => void
  onViewAllUpdates: () => void
}

const columnHelper = createColumnHelper<WorkdayProfileAttribute>()

export const ProfileDataView: FC<Props> = ({
  employeeId,
  date,
  onDateChange,
  onViewAllUpdates,
}) => {
  const { t } = useTranslation()
  const { data: profile, isLoading } = useWorkdayProfile(employeeId, date)

  // The input reports an empty value while a date is part-way typed,
  // so the table keeps showing the last complete date
  const [inputDate, setInputDate] = useState(date.toISODate() ?? '')

  const columns = [
    columnHelper.accessor('field', {
      header: t('features.usersAdmin.userDetails.workdayProfileData.field'),
      filterFn: 'includesString',
    }),
    columnHelper.accessor('value', {
      header: t('features.usersAdmin.userDetails.workdayProfileData.value'),
      filterFn: 'includesString',
      sortingFn: compareWorkdayValues,
      cell: ({ getValue }) => <WorkdayValueCell value={getValue()} />,
    }),
  ] as ColumnDef<WorkdayProfileAttribute, WorkdayValue>[]

  return (
    <div className="flex flex-col w-[640px] h-[65vh] px-6 pb-6 gap-4">
      <div className="flex items-end justify-between">
        <Field
          htmlFor="workday-profile-effective-date"
          label={t(
            'features.usersAdmin.userDetails.workdayProfileData.effectiveDate',
          )}
          leaveRoomForErrors={false}
        >
          {(id) => (
            <Input
              id={id}
              type="date"
              className="w-40"
              value={inputDate}
              onChange={(event) => {
                setInputDate(event.target.value)
                const value = DateTime.fromISO(event.target.value)
                if (value.isValid) onDateChange(value)
              }}
            />
          )}
        </Field>
        <Button variant="outlined" className="px-3" onClick={onViewAllUpdates}>
          {t(
            'features.usersAdmin.userDetails.workdayProfileData.viewAllUpdates',
          )}
        </Button>
      </div>
      {isLoading || !profile ? (
        <Spinner />
      ) : (
        <div className="flex flex-col flex-grow">
          <WorkdayDataTable
            columns={columns}
            data={profile.attributes}
            initialSorting={[{ id: 'field', desc: false }]}
          />
        </div>
      )}
    </div>
  )
}
